"use client";
import { useAuth } from "@/app/context/AuthContext";
import axios, { AxiosError } from "axios";
import { useState } from "react";
import { PasswordField } from "./PasswordField";
import { PALETTE } from "@/app/lib/colorPallete";
import { Loader2, Mail } from "lucide-react";
import { motion } from "framer-motion";

export const LoginForm = ({ onSwitchToRegister, onSwitchToForgot, onSuccess }: { onSwitchToRegister: () => void, onSwitchToForgot: () => void, onSuccess: () => void }) => {
    const { setUser } = useAuth();
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [validationErrors, setValidationErrors] = useState<Record<string, string[]>>({});

    const getFieldError = (field: string) => validationErrors[field]?.join(', ');

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setIsLoading(true);
        setError(null);
        setValidationErrors({});
        try {
            const response = await axios.post('/api/user/login', { email, password });
            if (response.data.user) {
                setUser(response.data.user);
                onSuccess();
            }
        } catch (err) {
            const axiosError = err as AxiosError<{ message: string, errors?: Record<string, string[]> }>;
            setError(axiosError.response?.data?.message || 'An error occurred.');
            if (axiosError.response?.data?.errors) {
                setValidationErrors(axiosError.response.data.errors);
            } else {
                setValidationErrors({});
            }
        } finally {
            setIsLoading(false);
        }
    };

    return (
        <motion.div key="login" initial={{ x: 0, opacity: 1 }} animate={{ x: 0, opacity: 1 }} exit={{ x: '-100%', opacity: 0 }} transition={{ type: 'tween', ease: 'easeInOut' }}>
            <h2 className="text-2xl font-bold text-center font-serif" style={{ color: PALETTE.darkGreen }}>Welcome Back</h2>
            <p className="text-center text-stone-500 mb-6 mt-2">Please log in to continue.</p>

            {error && !Object.keys(validationErrors).length && (
                <div className="bg-red-50 border border-red-200 rounded-lg p-3 mb-4">
                    <p className="text-sm text-red-600 text-center">{error}</p>
                </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-1.5">
                    <label className="block text-sm font-medium text-stone-600">Email Address</label>
                    <div className="relative">
                        <Mail size={18} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400 pointer-events-none" />
                        <input
                            type="email"
                            placeholder="you@example.com"
                            value={email}
                            onChange={(e) => setEmail(e.target.value)}
                            required
                            className={`w-full p-3 pl-10 rounded-md bg-stone-100 border focus:ring-2 transition-colors text-stone-900 placeholder:text-stone-400 ${
                                getFieldError('email') ? 'border-red-400 focus:ring-red-400/50' : 'border-stone-300 focus:ring-amber-500/80'
                            }`}
                        />
                    </div>
                    {getFieldError('email') && <p className="text-xs text-red-500">{getFieldError('email')}</p>}
                </div>

                <PasswordField
                    label="Password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    error={getFieldError('password')}
                />

                <div className="text-right text-xs">
                    <button type="button" onClick={onSwitchToForgot} className="font-semibold text-stone-500 hover:text-amber-600 hover:underline transition-colors">Forgot Password?</button>
                </div>

                <button type="submit" disabled={isLoading} className="w-full py-3 mt-2 rounded-full font-semibold text-white flex items-center justify-center gap-2 disabled:bg-stone-400 transition-all hover:scale-[1.02] active:scale-[0.98]" style={{ backgroundColor: PALETTE.darkerGreen }}>
                    {isLoading ? <><Loader2 className="animate-spin" size={20} /> Signing In...</> : 'Sign In'}
                </button>
            </form>
            <p className="text-center text-sm text-stone-500 mt-6">Don&apos;t have an account?{' '} <button onClick={onSwitchToRegister} className="font-semibold text-amber-600 hover:underline">Sign up</button></p>
        </motion.div>
    );
};
